import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { BasketItem as BasketItemType } from "@is/types";

const WARNING_MESSAGE_DURATION = 5000;

type Props = {
  item: BasketItemType;
  onChangeQuantity: (itemId: string, newQuantity: number) => void;
  onRemove: (itemId: string) => void;
  onClick: (item: BasketItemType) => void;
};

const useManageBasketItem = ({ item, onChangeQuantity, onClick }: Props) => {
  const { t } = useTranslation(TranslationContext.Basket);

  const [isLimitationWarningShown, setIsLimitationWarningShown] =
    useState<boolean>(false);

  const handleIncreaseQuantity = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isLimitationWarningShown) {
        return;
      }

      if (item.quantity >= item.onlineQuantity) {
        setIsLimitationWarningShown(true);

        trackEvent({
          eventType: TrackingEventTypes.FailedAddingToBasketDueToStockLimit,
          data: {
            ean: item.ean,
            quantity: 1,
            styleId: item.productId,
            variantId: item.variantId,
          },
        });
        return;
      }

      onChangeQuantity(item.ean, item.quantity + 1);
    },
    [isLimitationWarningShown, item, onChangeQuantity]
  );

  const handleDecreaseQuantity = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (item.quantity < 1) {
        return;
      }

      onChangeQuantity(item.ean, item.quantity - 1);
    },
    [item, onChangeQuantity]
  );

  const handleClick = useCallback(() => {
    onClick(item);
  }, [item, onClick]);

  const warningText = useMemo(
    () => t("Cannot add more of this item due to stock limit"),
    [t]
  );

  useEffect(() => {
    if (isLimitationWarningShown) {
      const timer = setTimeout(
        () => setIsLimitationWarningShown(false),
        WARNING_MESSAGE_DURATION
      );

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isLimitationWarningShown]);

  return {
    handleIncreaseQuantity,
    handleDecreaseQuantity,
    isLimitationWarningShown,
    warningText,
    handleClick,
  };
};

export default useManageBasketItem;
